import { FC, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { applyActionCode, Auth } from 'firebase/auth'
import { auth } from '../../../firebase'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sileo } from 'sileo'
import { UserAuth } from '../../../context/AuthContext'
import { OrganizationAPI } from '../../../api/organization.api'
import {
  getRegistrationPath,
  REGISTRATION_STATUS
} from '../../../utils/authSession'

const organizationApi = new OrganizationAPI()

const getErrorCode = (error: unknown) => {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return String((error as { code?: unknown }).code ?? 'unknown')
  }

  return 'unknown'
}

const getStoredFirebaseCredential = () => {
  try {
    const raw = localStorage.getItem('firebaseVerificationCredential')
    if (!raw) return null

    const parsed = JSON.parse(raw) as { uid?: unknown; email?: unknown }
    if (typeof parsed.uid !== 'string' || typeof parsed.email !== 'string') return null

    return { uid: parsed.uid, email: parsed.email }
  } catch {
    return null
  }
}

const getFirebaseCredential = (auth: Auth) => {
  const currentUser = auth.currentUser
  if (currentUser?.uid && currentUser.email) {
    return { uid: currentUser.uid, email: currentUser.email }
  }

  return getStoredFirebaseCredential()
}

const verifyOrganization = async (auth: Auth) => {
  const credential = getFirebaseCredential(auth)
  if (!credential) return

  await organizationApi.verification(credential)
  localStorage.removeItem('firebaseVerificationCredential')
}

export const ActionAuth: FC = () => {
  const [urlParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, isLoaded, refreshBackendSession } = UserAuth()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isVerified, setIsVerified] = useState(false)

  const handleEmailVerification = async (auth: Auth, oobCode: string) => {
    try {
      await applyActionCode(auth, oobCode)
      localStorage.setItem('isValidated', 'true')
      sileo.success({
        title: 'Correo Verificado',
        description: (
          <span className='text-green-500/80 font-medium'>
            Correo exitosamente verificado. Ahora completa tu registro 👌
          </span>
        )
      })

      await verifyOrganization(auth)
      if (auth.currentUser) {
        await auth.currentUser.reload()
        const session = await refreshBackendSession(auth.currentUser)

        if (session.registrationStatus === REGISTRATION_STATUS.COMPLETED) {
          navigate('/complete-signup/poll-sii', { replace: true })
        } else {
          navigate(getRegistrationPath(session.registrationStatus), { replace: true })
        }
      }
      setIsVerified(true)
    } catch (error) {
      setError(getErrorCode(error))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const oobCode = urlParams.get('oobCode') ?? ''
    const mode = urlParams.get('mode') ?? ''
    if (!oobCode && !mode) {
      setIsVerified(true)
      setIsLoading(false)
      return
    }
    if (mode === 'resetPassword') {
      navigate(`/auth/complete-reset-password?mode=${mode}&oobCode=${oobCode}`, { replace: true })
      return
    }
    if (mode === 'verifyEmail') {
      handleEmailVerification(auth, oobCode)
      return
    }
  }, [])

  useEffect(() => {
    if (isLoaded && !user?.emailVerified && error) {
      sileo.error({
        title: 'Error de Verificación',
        description: (
          <span className='text-red-500/80 font-medium'>
            No se pudo verificar el correo. Intenta reenviarlo nuevamente 🤯
          </span>
        )
      })
      setIsVerified(false)
    }
  }, [user, error, isLoaded])

  return (
    <div className='flex flex-col gap-6 justify-between mb-12 bg-white rounded-lg shadow-md px-7 py-9 w-[460px]'>
      <div className='mb-6'>
        <h3 className='mb-3 text-3xl font-bold text-center'>
          {isLoading
            ? 'Cargando...'
            : error && !user?.emailVerified
              ? 'Error, Lo sentimos'
              : isVerified
                ? 'Correo Verificado'
              : urlParams.get('mode') === 'verifyEmail'
                ? 'Correo Verificado'
                : 'Reestablecer contraseña'}
        </h3>
        <p>
          {isVerified
            ? 'Tu correo fue verificado correctamente. Favor volver a la página de registro para continuar.'
            : 'Tu cuenta está casi lista. Completa los siguientes pasos para terminar el proceso.'}
        </p>
      </div>

      <div>
        {isLoading ? (
          <button disabled type='button' className='btn-primary rounded-full'>
            <span>Cargando</span>
            <FontAwesomeIcon className="" spin={true} icon={faCircleNotch} />
          </button>
        ) : // Si la verificación fue exitosa, la pestaña se cerrará automáticamente.
        // En caso de error se redirigirá a la página de registro.
        null}
      </div>
    </div>
  )
}
